import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { reportSchedulesAPI, dashboardAPI, campaignsAPI, campaignGroupsAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Send,
  Mail,
  Clock,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Search,
  FileText,
  FileSpreadsheet,
  Paperclip,
} from 'lucide-react';

export const ReportSchedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignGroups, setCampaignGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [campaignSearchQuery, setCampaignSearchQuery] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    name: 'Report Schedule',
    recipient_email: '',
    recipient_name: '',
    recipients: [],
    frequency: 'daily',
    schedule_hour: 8,
    schedule_minute: 0,
    day_of_week: 0,  // Monday
    day_of_month: 1,
    platforms: [],
    date_range_type: 'last_7_days',
    is_active: true,
    selected_campaign_ids: [],
    selected_group_ids: [],
    export_formats: [],  // ['pdf', 'excel'] or empty for HTML only
    include_charts: true,
  });
  const [sendFormData, setSendFormData] = useState({
    recipient_email: '',
    recipient_name: '',
    platforms: [],
    export_formats: [],  // ['pdf', 'excel'] for attachments
  });
  const [additionalRecipient, setAdditionalRecipient] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const FREQUENCIES = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Bi-weekly' },
    { value: 'monthly', label: 'Monthly' },
  ];

  const DAYS_OF_WEEK = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' },
  ];

  const DATE_RANGES = [
    { value: 'last_24_hours', label: 'Last 24 Hours' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_15_days', label: 'Last 15 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'last_month', label: 'Last Month' },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [schedulesRes, platformsRes, emailStatusRes, campaignsRes, groupsRes] = await Promise.all([
        reportSchedulesAPI.getAll(),
        dashboardAPI.getPlatforms(),
        reportSchedulesAPI.getEmailStatus(),
        campaignsAPI.getAll().catch(() => ({ data: [] })),
        campaignGroupsAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setSchedules(schedulesRes.data);
      setPlatforms(platformsRes.data.platforms);
      setEmailConfigured(emailStatusRes.data.configured);
      setCampaigns(campaignsRes.data || []);
      setCampaignGroups(groupsRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateDialog = () => {
    setEditingSchedule(null);
    setFormData({
      name: 'Report Schedule',
      recipient_email: '',
      recipient_name: '',
      recipients: [],
      frequency: 'daily',
      schedule_hour: 8,
      schedule_minute: 0,
      day_of_week: 0,
      day_of_month: 1,
      platforms: [],
      date_range_type: 'last_7_days',
      is_active: true,
      selected_campaign_ids: [],
      selected_group_ids: [],
      export_formats: [],
      include_charts: true,
    });
    setAdditionalRecipient('');
    setCampaignSearchQuery('');
    setDialogOpen(true);
  };

  const openEditDialog = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      name: schedule.name || 'Report Schedule',
      recipient_email: schedule.recipient_email,
      recipient_name: schedule.recipient_name,
      recipients: schedule.recipients || [],
      frequency: schedule.frequency,
      schedule_hour: schedule.schedule_time?.hour || 8,
      schedule_minute: schedule.schedule_time?.minute || 0,
      day_of_week: schedule.day_of_week ?? 0,
      day_of_month: schedule.day_of_month ?? 1,
      platforms: schedule.platforms || [],
      date_range_type: schedule.filters?.date_range_type || 'last_7_days',
      is_active: schedule.is_active,
      export_formats: schedule.filters?.export_formats || [],
      include_charts: schedule.filters?.include_charts ?? true,
    });
    setAdditionalRecipient('');
    setDialogOpen(true);
  };

  const addRecipient = () => {
    if (additionalRecipient && !formData.recipients.includes(additionalRecipient)) {
      setFormData(prev => ({
        ...prev,
        recipients: [...prev.recipients, additionalRecipient]
      }));
      setAdditionalRecipient('');
    }
  };

  const removeRecipient = (email) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r !== email)
    }));
  };

  const openSendDialog = () => {
    setSendFormData({
      recipient_email: '',
      recipient_name: '',
      platforms: [],
      export_formats: [],
    });
    setSendDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Build the data with advanced scheduling options
      const submitData = {
        name: formData.name,
        recipient_email: formData.recipient_email,
        recipient_name: formData.recipient_name,
        recipients: formData.recipients.length > 0 ? formData.recipients : [formData.recipient_email],
        frequency: formData.frequency,
        schedule_time: {
          hour: parseInt(formData.schedule_hour),
          minute: parseInt(formData.schedule_minute),
          timezone: 'UTC'
        },
        day_of_week: formData.frequency === 'weekly' || formData.frequency === 'biweekly' ? formData.day_of_week : null,
        day_of_month: formData.frequency === 'monthly' ? formData.day_of_month : null,
        platforms: formData.platforms,
        filters: {
          platforms: formData.platforms,
          date_range_type: formData.date_range_type,
          export_formats: formData.export_formats,
          include_charts: formData.include_charts,
        },
        is_active: formData.is_active,
      };

      if (editingSchedule) {
        await reportSchedulesAPI.update(editingSchedule.id, submitData);
        toast.success('Schedule updated successfully');
      } else {
        await reportSchedulesAPI.create(submitData);
        toast.success('Schedule created successfully');
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReport = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await reportSchedulesAPI.sendReport(sendFormData);
      toast.success(`Report is being sent to ${sendFormData.recipient_email}`);
      setSendDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (schedule) => {
    if (!window.confirm(`Are you sure you want to delete this schedule?`)) {
      return;
    }

    try {
      await reportSchedulesAPI.delete(schedule.id);
      toast.success('Schedule deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete schedule');
    }
  };

  const handleToggleActive = async (schedule) => {
    try {
      await reportSchedulesAPI.update(schedule.id, { is_active: !schedule.is_active });
      toast.success(`Schedule ${schedule.is_active ? 'paused' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  const togglePlatform = (platform, formType = 'schedule') => {
    if (formType === 'schedule') {
      setFormData((prev) => ({
        ...prev,
        platforms: prev.platforms.includes(platform)
          ? prev.platforms.filter((p) => p !== platform)
          : [...prev.platforms, platform],
      }));
    } else {
      setSendFormData((prev) => ({
        ...prev,
        platforms: prev.platforms.includes(platform)
          ? prev.platforms.filter((p) => p !== platform)
          : [...prev.platforms, platform],
      }));
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Report Schedules</h1>
            <p className="text-sm text-slate-500 mt-1">Manage automated daily report emails</p>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={openSendDialog} disabled={!emailConfigured} data-testid="send-report-now-btn">
              <Send className="w-4 h-4 mr-2" />
              Send Report Now
            </Button>
            <Button onClick={openCreateDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="create-schedule-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Schedule
            </Button>
          </div>
        </div>

        {/* Email Status */}
        <Card className={`border ${emailConfigured ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {emailConfigured ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-emerald-900">Email Service Connected</p>
                    <p className="text-sm text-emerald-700">Reports will be sent from onboarding@resend.dev</p>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-900">Email Service Not Configured</p>
                    <p className="text-sm text-amber-700">Add RESEND_API_KEY to enable email reports</p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Schedules Table */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Scheduled Reports
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Recipient</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Frequency</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Platforms</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Sent</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        {Array.from({ length: 7 }).map((_, j) => (
                          <td key={j} className="py-4 px-4">
                            <div className="h-4 bg-slate-200 animate-pulse rounded" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : schedules.length > 0 ? (
                    schedules.map((schedule) => (
                      <tr key={schedule.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                              <Mail className="w-4 h-4 text-slate-600" />
                            </div>
                            <span className="font-medium text-slate-900">{schedule.recipient_name}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-slate-600">{schedule.recipient_email}</td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className="capitalize text-slate-700">{schedule.frequency}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-slate-600 text-sm">
                            {schedule.platforms?.length > 0
                              ? schedule.platforms.join(', ')
                              : 'All Platforms'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${schedule.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                            {schedule.is_active ? 'Active' : 'Paused'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-sm text-slate-500">
                          {schedule.last_sent ? formatDate(schedule.last_sent) : 'Never'}
                        </td>
                        <td className="text-right py-4 px-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`schedule-actions-${schedule.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(schedule)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleActive(schedule)}>
                                {schedule.is_active ? 'Pause' : 'Activate'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(schedule)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-400">
                        No report schedules configured. Create one to start sending automated reports.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit Schedule Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingSchedule ? 'Edit Schedule' : 'Create Report Schedule'}
              </DialogTitle>
              <DialogDescription>
                Configure automated report emails with advanced scheduling
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                {/* Schedule Name */}
                <div className="space-y-2">
                  <Label htmlFor="schedule_name">Schedule Name</Label>
                  <Input
                    id="schedule_name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Weekly Performance Report"
                    data-testid="schedule-name-input"
                  />
                </div>

                {/* Primary Recipient */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="recipient_name">Recipient Name</Label>
                    <Input
                      id="recipient_name"
                      value={formData.recipient_name}
                      onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="recipient_email">Email</Label>
                    <Input
                      id="recipient_email"
                      type="email"
                      value={formData.recipient_email}
                      onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                      placeholder="john@company.com"
                      required
                      data-testid="schedule-email-input"
                    />
                  </div>
                </div>

                {/* Additional Recipients */}
                <div className="space-y-2">
                  <Label>Additional Recipients</Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={additionalRecipient}
                      onChange={(e) => setAdditionalRecipient(e.target.value)}
                      placeholder="Add another email..."
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" onClick={addRecipient}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {formData.recipients.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.recipients.map(email => (
                        <span key={email} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                          {email}
                          <button type="button" onClick={() => removeRecipient(email)} className="hover:text-red-500">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Frequency & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select
                      value={formData.frequency}
                      onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                    >
                      <SelectTrigger data-testid="schedule-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Time (UTC)</Label>
                    <div className="flex gap-2">
                      <Select
                        value={String(formData.schedule_hour)}
                        onValueChange={(value) => setFormData({ ...formData, schedule_hour: parseInt(value) })}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="flex items-center">:</span>
                      <Select
                        value={String(formData.schedule_minute)}
                        onValueChange={(value) => setFormData({ ...formData, schedule_minute: parseInt(value) })}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 15, 30, 45].map(m => (
                            <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Day Selection (for weekly/biweekly/monthly) */}
                {(formData.frequency === 'weekly' || formData.frequency === 'biweekly') && (
                  <div className="space-y-2">
                    <Label>Day of Week</Label>
                    <Select
                      value={String(formData.day_of_week)}
                      onValueChange={(value) => setFormData({ ...formData, day_of_week: parseInt(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map(d => (
                          <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.frequency === 'monthly' && (
                  <div className="space-y-2">
                    <Label>Day of Month</Label>
                    <Select
                      value={String(formData.day_of_month)}
                      onValueChange={(value) => setFormData({ ...formData, day_of_month: parseInt(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Date Range for Report */}
                <div className="space-y-2">
                  <Label>Report Date Range</Label>
                  <Select
                    value={formData.date_range_type}
                    onValueChange={(value) => setFormData({ ...formData, date_range_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_RANGES.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Platforms */}
                <div className="space-y-2">
                  <Label>Platforms (leave empty for all)</Label>
                  <div className="grid grid-cols-3 gap-2 p-3 border rounded-md bg-slate-50">
                    {platforms.map((platform) => (
                      <label
                        key={platform}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <Checkbox
                          checked={formData.platforms.includes(platform)}
                          onCheckedChange={() => togglePlatform(platform, 'schedule')}
                        />
                        {platform}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Campaign Selection */}
                <div className="space-y-3">
                  <Label>Filter by Campaigns (optional)</Label>
                  <p className="text-xs text-slate-500">Leave empty to include all campaigns in the report</p>
                  
                  {/* Campaign Groups Selection */}
                  {campaignGroups.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600">Campaign Groups</Label>
                      <div className="max-h-32 overflow-y-auto p-2 border rounded-md bg-slate-50">
                        {campaignGroups.map((group) => (
                          <label key={group.id} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                            <Checkbox
                              checked={formData.selected_group_ids?.includes(group.id)}
                              onCheckedChange={(checked) => {
                                const newGroups = checked 
                                  ? [...(formData.selected_group_ids || []), group.id]
                                  : (formData.selected_group_ids || []).filter(id => id !== group.id);
                                setFormData({ ...formData, selected_group_ids: newGroups });
                              }}
                            />
                            <span>{group.name}</span>
                            <span className="text-xs text-slate-400">({group.linked_campaigns?.length || 0} campaigns)</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Individual Campaigns Selection */}
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-600">Individual Campaigns</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search campaigns..."
                        value={campaignSearchQuery}
                        onChange={(e) => setCampaignSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto p-2 border rounded-md bg-slate-50">
                      {campaigns
                        .filter(c => !campaignSearchQuery || c.name.toLowerCase().includes(campaignSearchQuery.toLowerCase()))
                        .slice(0, 50)
                        .map((campaign) => (
                          <label key={campaign.id} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                            <Checkbox
                              checked={formData.selected_campaign_ids?.includes(campaign.id)}
                              onCheckedChange={(checked) => {
                                const newCampaigns = checked 
                                  ? [...(formData.selected_campaign_ids || []), campaign.id]
                                  : (formData.selected_campaign_ids || []).filter(id => id !== campaign.id);
                                setFormData({ ...formData, selected_campaign_ids: newCampaigns });
                              }}
                            />
                            <span className="truncate">{campaign.name}</span>
                          </label>
                        ))}
                      {campaigns.length > 50 && (
                        <p className="text-xs text-slate-400 py-1">Use search to find more campaigns...</p>
                      )}
                    </div>
                    {(formData.selected_campaign_ids?.length > 0 || formData.selected_group_ids?.length > 0) && (
                      <p className="text-xs text-blue-600">
                        Selected: {formData.selected_group_ids?.length || 0} groups, {formData.selected_campaign_ids?.length || 0} campaigns
                      </p>
                    )}
                  </div>
                </div>

                {/* Export Format Selection */}
                <div className="space-y-3 p-4 border rounded-lg bg-blue-50/50 border-blue-200">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-blue-600" />
                    <Label className="text-sm font-medium text-blue-900">Email Attachments (Reports with Charts)</Label>
                  </div>
                  <p className="text-xs text-blue-700">Select formats to attach graphical reports with charts to scheduled emails</p>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.export_formats?.includes('pdf')}
                        onCheckedChange={(checked) => {
                          const formats = formData.export_formats || [];
                          setFormData({
                            ...formData,
                            export_formats: checked
                              ? [...formats, 'pdf']
                              : formats.filter(f => f !== 'pdf')
                          });
                        }}
                      />
                      <FileText className="w-4 h-4 text-red-600" />
                      <span className="text-sm">PDF Report</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.export_formats?.includes('excel')}
                        onCheckedChange={(checked) => {
                          const formats = formData.export_formats || [];
                          setFormData({
                            ...formData,
                            export_formats: checked
                              ? [...formats, 'excel']
                              : formats.filter(f => f !== 'excel')
                          });
                        }}
                      />
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      <span className="text-sm">Excel Report</span>
                    </label>
                  </div>
                  {formData.export_formats?.length > 0 && (
                    <p className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      {formData.export_formats.length} attachment(s) will be included with each email
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="is_active">Active</Label>
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              </div>

              <DialogFooter className="flex flex-row justify-end gap-2 sm:gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="schedule-save-btn">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : editingSchedule ? (
                    'Update Schedule'
                  ) : (
                    'Create Schedule'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Send Report Now Dialog */}
        <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Send Report Now</DialogTitle>
              <DialogDescription>
                Send an immediate report email with yesterday's data
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSendReport}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="send_recipient_name">Recipient Name</Label>
                  <Input
                    id="send_recipient_name"
                    value={sendFormData.recipient_name}
                    onChange={(e) => setSendFormData({ ...sendFormData, recipient_name: e.target.value })}
                    placeholder="John Doe"
                    required
                    data-testid="send-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="send_recipient_email">Recipient Email</Label>
                  <Input
                    id="send_recipient_email"
                    type="email"
                    value={sendFormData.recipient_email}
                    onChange={(e) => setSendFormData({ ...sendFormData, recipient_email: e.target.value })}
                    placeholder="john@company.com"
                    required
                    data-testid="send-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Platforms (leave empty for all)</Label>
                  <div className="grid grid-cols-3 gap-2 p-3 border rounded-md bg-slate-50">
                    {platforms.map((platform) => (
                      <label
                        key={platform}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <Checkbox
                          checked={sendFormData.platforms.includes(platform)}
                          onCheckedChange={() => togglePlatform(platform, 'send')}
                        />
                        {platform}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Export Format Selection for Send Now */}
                <div className="space-y-3 p-3 border rounded-lg bg-blue-50/50 border-blue-200">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-blue-600" />
                    <Label className="text-sm font-medium text-blue-900">Attach Reports with Charts</Label>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={sendFormData.export_formats?.includes('pdf')}
                        onCheckedChange={(checked) => {
                          const formats = sendFormData.export_formats || [];
                          setSendFormData({
                            ...sendFormData,
                            export_formats: checked
                              ? [...formats, 'pdf']
                              : formats.filter(f => f !== 'pdf')
                          });
                        }}
                      />
                      <FileText className="w-4 h-4 text-red-600" />
                      <span className="text-sm">PDF</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={sendFormData.export_formats?.includes('excel')}
                        onCheckedChange={(checked) => {
                          const formats = sendFormData.export_formats || [];
                          setSendFormData({
                            ...sendFormData,
                            export_formats: checked
                              ? [...formats, 'excel']
                              : formats.filter(f => f !== 'excel')
                          });
                        }}
                      />
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      <span className="text-sm">Excel</span>
                    </label>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex flex-row justify-end gap-2 sm:gap-2">
                <Button type="button" variant="outline" onClick={() => setSendDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={sending} className="bg-slate-900 hover:bg-slate-800" data-testid="send-report-btn">
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Report
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
